import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import {
  browserPushState,
  disableBrowserPush,
  enableBrowserPush,
  PushPermissionDenied,
  type BrowserPushState,
} from './browserPush'
import { fetchPushOverview, removeDevice, type PushOverview } from './pushApi'

/**
 * «Уведомления в браузере» и список своих устройств (тикет #120).
 *
 * Кнопка не обещает больше, чем выйдет: без ключей VAPID на сервере, в браузере без push или
 * при запрещённых сайту уведомлениях она не показывается, а причина называется словами.
 * Устройство, которое служба push объявила ушедшим, остаётся в списке с причиной — иначе
 * «почему перестало приходить» не на что посмотреть.
 */
export function BrowserPushPanel() {
  const { t } = useApp()
  const [overview, setOverview] = useState<PushOverview | null>(null)
  const [state, setState] = useState<BrowserPushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = async (signal?: AbortSignal) => {
    const [next, local] = await Promise.all([fetchPushOverview(signal), browserPushState()])
    setOverview(next)
    setState(local)
  }

  useEffect(() => {
    const controller = new AbortController()
    reload(controller.signal).catch((failure: unknown) => {
      if (controller.signal.aborted) return
      setError(failure instanceof ApiError ? failure.message : 'app.error')
    })
    return () => controller.abort()
  }, [])

  const run = (action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    action()
      .then(() => reload())
      .catch((failure: unknown) => {
        if (failure instanceof PushPermissionDenied) {
          setState('denied')
          return
        }
        setError(failure instanceof ApiError ? failure.message : 'notifications.push.failed')
      })
      .finally(() => setBusy(false))
  }

  const errorText = error === 'app.error' || error === 'notifications.push.failed' ? t(error) : error

  return (
    <div className="stack" style={{ gap: 8 }} aria-label={t('notifications.push.title')} role="group">
      <span className="h3">{t('notifications.push.title')}</span>
      {overview == null || state == null ? (
        error ? null : <span className="muted" aria-busy="true">{t('notifications.settings.loading')}</span>
      ) : (
        <>
          {!overview.webPushAvailable ? (
            <span className="t-sm muted" role="note">{t('notifications.push.notConnected')}</span>
          ) : state === 'unsupported' ? (
            <span className="t-sm muted" role="note">{t('notifications.push.unsupported')}</span>
          ) : state === 'denied' ? (
            <div className="alert alert-warning" role="note">
              <Icon name="alert" />
              <span>{t('notifications.push.denied')}</span>
            </div>
          ) : state === 'on' ? (
            <div className="row" style={{ gap: 8 }}>
              <span className="t-sm" role="status">{t('notifications.push.on')}</span>
              <button className="btn btn-sm" type="button" disabled={busy}
                      onClick={() => run(disableBrowserPush)}>
                {t('notifications.push.disable')}
              </button>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" type="button" disabled={busy || overview.webPushPublicKey == null}
                    onClick={() => run(() => enableBrowserPush(overview.webPushPublicKey ?? ''))}>
              {t('notifications.push.enable')}
            </button>
          )}

          {overview.devices.length > 0 && (
            <ul className="stack" style={{ gap: 4, margin: 0, paddingLeft: 16 }} aria-label={t('notifications.push.devices')}>
              {overview.devices.map((device) => (
                <li key={device.id} className="t-sm">
                  <span>{device.label ?? t(`notifications.push.channel.${device.channel}`)}</span>
                  {' · '}
                  <span className="muted">{t(`notifications.push.channel.${device.channel}`)}</span>
                  {device.disabledReason != null && (
                    <span className="muted">{' · '}{t('notifications.push.disabled', { reason: device.disabledReason })}</span>
                  )}
                  {' '}
                  <button className="btn btn-ghost btn-sm" type="button" disabled={busy}
                          aria-label={t('notifications.push.remove', { device: device.label ?? device.channel })}
                          onClick={() => run(() => removeDevice(device.id))}>
                    {t('notifications.push.removeShort')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {errorText && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{errorText}</span>
        </div>
      )}
    </div>
  )
}
